import { makeArticle, saintQuestionRelated } from "./learnFactory";

export const pageSaintQuestions = makeArticle({
  slug: "saint-tms-questions-answered",
  title: "SAINT® TMS Questions Answered | Neuro Wellness",
  description:
    "30 SAINT® TMS questions answered: how it works, how it feels, MRI targeting, sessions, driving, work, outpatient, side effects, eligibility, and what happens after treatment.",
  canonical: "/learn/saint-tms-questions-answered",
  h1: "SAINT® TMS: Answers to the Most Common Patient Questions",
  breadcrumbLabel: "SAINT® TMS Questions",
  shortAnswer:
    "SAINT® (Stanford Accelerated Intelligent Neuromodulation Therapy) is an FDA-cleared, fMRI-guided accelerated TMS protocol that delivers 50 sessions over five consecutive days. It uses magnetic pulses — no surgery, no anesthesia, no medication. Patients are awake, can drive after sessions, and receive treatment in an outpatient setting. This article answers the most common questions patients ask about SAINT®.",
  keyTakeaways: [
    "SAINT® is FDA-cleared for adults with major depressive disorder who haven't improved on antidepressants.",
    "Treatment is 50 sessions over 5 consecutive days, with 10 sessions per day.",
    "Targeting uses each patient's own functional MRI (fMRI).",
    "No anesthesia, no medication, no hospitalization required.",
    "Patients are awake and can drive after sessions.",
  ],
  sections: [
    {
      heading: "What Is SAINT TMS?",
      paragraphs: [
        "SAINT® (Stanford Accelerated Intelligent Neuromodulation Therapy) is an accelerated form of transcranial magnetic stimulation (TMS) developed at Stanford University. It is FDA-cleared (510(k) K220177, September 2022) for adults with major depressive disorder who have not achieved satisfactory improvement from prior antidepressant medication in the current episode. The commercial version is the Magnus Neuromodulation System with SAINT Technology.",
      ],
    },
    {
      heading: "How Does SAINT TMS Work?",
      paragraphs: [
        "SAINT® uses intermittent theta burst stimulation (iTBS) — bursts of magnetic pulses delivered to a specific brain region. The stimulation target is chosen from each patient's own functional MRI (fMRI), using a proprietary algorithm that identifies the optimal site based on individual brain connectivity. This is different from conventional TMS, which uses scalp measurements to estimate the target.",
      ],
    },
    {
      heading: "How Is SAINT Different From Regular TMS?",
      paragraphs: [
        "Regular TMS delivers one session per day, five days a week, for about six weeks (36 sessions), targeted by scalp measurement. SAINT® delivers ten sessions per day for five consecutive days (50 sessions), targeted by fMRI. SAINT® uses a theta burst pulse pattern with 1,800 pulses per session. Regular TMS has a larger evidence base; SAINT® has two small single-site studies.",
      ],
    },
    {
      heading: "How Fast Does SAINT Work?",
      paragraphs: [
        "The SAINT® protocol itself is delivered over five days. In the 2022 sham-controlled trial, 78.6% of the active group reached remission at four weeks. In the 2020 open-label study, the average time to remission was 2.6 days. However, individual response varies, and the studies were small with short follow-up. Never assume a specific timeline for your own response.",
      ],
    },
    {
      heading: "How Long Does SAINT Treatment Take?",
      paragraphs: [
        "The treatment itself takes five consecutive days. Each day includes ten sessions of about ten minutes, separated by breaks of roughly fifty minutes. You should expect to spend most of each day at the clinic. Before treatment, you also need a psychiatric evaluation and an MRI, scheduled ahead of the treatment week.",
      ],
    },
    {
      heading: "How Many SAINT Sessions Are There?",
      paragraphs: [
        "SAINT® delivers 50 sessions total: ten sessions per day for five consecutive days. Each session is about ten minutes long, with 50-minute breaks between sessions. The total number of pulses per session is 1,800.",
      ],
    },
    {
      heading: "Why Are There Multiple SAINT Sessions Per Day?",
      paragraphs: [
        "SAINT® delivers ten sessions per day with 50-minute breaks based on research suggesting that intermittent theta burst stimulation with rest periods produces a faster and larger effect than once-daily stimulation. The breaks allow the brain to respond to stimulation between sessions. This accelerated schedule is what compresses the course from six weeks to five days.",
      ],
    },
    {
      heading: "Does SAINT Require an MRI?",
      paragraphs: [
        "Yes. SAINT® uses a structural and functional MRI (fMRI) to select an individualized stimulation target. The MRI must be completed and processed before the first treatment day. Standard MRI safety screening applies, and people who cannot safely have an MRI are not candidates for SAINT®.",
      ],
    },
    {
      heading: "What Type of MRI Is Used for SAINT?",
      paragraphs: [
        "SAINT® uses both a structural MRI (to map brain anatomy) and a functional MRI (to measure brain activity and connectivity). The fMRI data is processed by a proprietary algorithm that identifies the optimal stimulation target for each individual patient based on how their prefrontal cortex connects to deeper mood circuits.",
      ],
    },
    {
      heading: "How Does fMRI Targeting Work?",
      paragraphs: [
        "During a functional MRI, brain activity is measured while you rest. The data is processed by an algorithm that maps how different brain regions connect. The algorithm identifies the specific point on your left dorsolateral prefrontal cortex that has the strongest connection to brain circuits involved in mood regulation. This personalized target is different for each person.",
      ],
    },
    {
      heading: "What Part of the Brain Does SAINT Target?",
      paragraphs: [
        "SAINT® targets the left dorsolateral prefrontal cortex — a region involved in mood regulation, executive function, and attention. However, the exact stimulation point within this region is personalized for each patient based on their fMRI connectivity, rather than using a standard landmark measurement.",
      ],
    },
    {
      heading: "Does SAINT Hurt?",
      paragraphs: [
        "Most people describe a tapping or knocking sensation on the scalp during pulses. Headache was the most common side effect in the sham-controlled trial. Scalp discomfort and facial twitching are also common and usually lessen over the first day or two. Patients are awake, no anesthesia is used, and over-the-counter pain relief is often enough for headaches.",
      ],
    },
    {
      heading: "What Does SAINT Feel Like?",
      paragraphs: [
        "During each session, you feel a tapping or knocking sensation on the scalp as the magnetic coil delivers pulses. The sensation is unusual but not typically painful. Facial muscles may twitch during stimulation. Between sessions, during the 50-minute breaks, you feel normal and can read, work, rest, or socialize.",
      ],
    },
    {
      heading: "What Happens Between SAINT Sessions?",
      paragraphs: [
        "Between each of the ten daily sessions, there is a 50-minute break. During breaks, patients typically read, work on a laptop, rest, or socialize. You remain at the clinic for most of the day. The breaks are part of the protocol — they allow the brain to respond to stimulation between sessions.",
      ],
    },
    {
      heading: "Can I Drive After SAINT Treatment?",
      paragraphs: [
        "Yes, in most cases. SAINT® does not use anesthesia or sedation, so patients are generally permitted to drive after sessions. If you experience a side effect that affects your ability to drive, such as a significant headache or dizziness, tell the treatment team. A companion is optional, not required.",
      ],
    },
    {
      heading: "Can I Work During SAINT Treatment Week?",
      paragraphs: [
        "Full-time in-person work is usually not practical because you are at the clinic for most of each day. However, many patients work remotely during the 50-minute breaks between sessions. The breaks are long enough for email, calls, or focused work. Discuss your work situation with the treatment team during your consultation.",
      ],
    },
    {
      heading: "Can Someone Travel With Me for SAINT?",
      paragraphs: [
        "Yes. A family member, friend, or caregiver can accompany you. While not medically required, a companion can provide support. They cannot be in the treatment room during sessions but can be with you during the 50-minute breaks. Many patients traveling from out of town bring a companion.",
      ],
    },
    {
      heading: "Can I Receive SAINT as an Outpatient?",
      paragraphs: [
        "Yes. SAINT® is delivered in an outpatient setting at Neuro Wellness TMS Centers of America in Coral Springs, Florida. No hospitalization is required. Patients stay at a nearby hotel or commute daily, depending on their location.",
      ],
    },
    {
      heading: "Do I Need to Be Hospitalized for SAINT?",
      paragraphs: [
        "No. SAINT® is an outpatient treatment. No hospitalization is required. Patients receive treatment at the clinic during the day and return to their hotel or home each evening. This is different from ECT, which may require hospital-based treatment.",
      ],
    },
    {
      heading: "Does SAINT Require Anesthesia?",
      paragraphs: [
        "No. SAINT® does not require anesthesia or sedation. Patients are fully awake throughout treatment. This is a key difference from ECT, which requires general anesthesia for each session.",
      ],
    },
    {
      heading: "Does SAINT Require Medication?",
      paragraphs: [
        "No. SAINT® uses magnetic pulses, not medication. It introduces no chemical into the body. However, many patients continue their current antidepressant medication during and after SAINT®. Whether to continue, adjust, or discontinue medication is a clinical decision made by your psychiatrist.",
      ],
    },
    {
      heading: "What Are the Side Effects of SAINT?",
      paragraphs: [
        "The most common side effects are headache, scalp discomfort at the treatment site, and facial muscle twitching during stimulation. These usually lessen over the first day or two. The most serious risk is seizure, which is rare. SAINT® does not cause the systemic side effects of antidepressants (weight gain, sexual dysfunction, GI issues) or the memory effects of ECT.",
      ],
    },
    {
      heading: "Who Should Not Receive SAINT?",
      paragraphs: [
        "People with ferromagnetic metal in or near the head (such as cochlear implants or aneurysm clips), implanted electronic stimulators, or who cannot have an MRI are generally not candidates. A history of seizures requires careful evaluation and may rule out treatment. SAINT® is not FDA-cleared for bipolar depression, PTSD, anxiety disorders alone, OCD, or anyone under 18.",
      ],
    },
    {
      heading: "Who Qualifies for SAINT?",
      paragraphs: [
        "The FDA-cleared indication is adults with major depressive disorder who have not achieved satisfactory improvement from prior antidepressant medication in the current episode. Eligibility also depends on TMS and MRI safety screening. A psychiatric evaluation determines whether SAINT® is appropriate for a specific person.",
      ],
    },
    {
      heading: "Do I Need Treatment-Resistant Depression to Receive SAINT?",
      paragraphs: [
        "SAINT® is cleared for adults whose depression has not improved adequately on antidepressant medication. This overlaps with common definitions of treatment-resistant depression (inadequate response to at least two antidepressant trials). However, the FDA-cleared indication does not specify a minimum number of failed trials — it refers to not achieving satisfactory improvement from prior antidepressant medication.",
      ],
    },
    {
      heading: "How Quickly Can I Start SAINT?",
      paragraphs: [
        "Self-pay patients can often start SAINT® within days to weeks, depending on scheduling availability and MRI availability. If insurance authorization is needed, the timeline may be longer. The pre-treatment evaluation and MRI must be completed before the treatment week begins. Contact the clinic for current scheduling availability.",
      ],
    },
    {
      heading: "What Happens After the Five-Day SAINT Protocol?",
      paragraphs: [
        "After the five-day treatment week, a follow-up assessment is conducted to evaluate response. A maintenance plan is discussed, which may include ongoing medication, psychotherapy, and follow-up visits. The 2022 trial followed participants for four weeks; longer-term durability is not yet well established.",
      ],
    },
    {
      heading: "Can SAINT Be Repeated?",
      paragraphs: [
        "Yes, SAINT® can be repeated if symptoms return. Open-label follow-up suggested that some people who improved saw symptoms return within months and were retreated. Anyone considering SAINT® should discuss a maintenance plan with their psychiatrist, including the possibility of another course.",
      ],
    },
    {
      heading: "What Happens If Depression Returns After SAINT?",
      paragraphs: [
        "If depression returns after SAINT®, options include medication adjustments, psychotherapy, a repeat SAINT® course, or other treatments. The 2022 trial had only four weeks of follow-up, so long-term durability is not well established. Discuss a maintenance plan with your psychiatrist before starting treatment.",
      ],
    },
  ],
  faqs: [
    {
      question: "What is SAINT TMS?",
      answer:
        "SAINT® (Stanford Accelerated Intelligent Neuromodulation Therapy) is an FDA-cleared, fMRI-guided accelerated TMS protocol that delivers 50 sessions over five consecutive days. It uses magnetic pulses — no surgery, no anesthesia, no medication. It is cleared for adults with major depressive disorder who haven't improved on antidepressants.",
    },
    {
      question: "Does SAINT TMS hurt?",
      answer:
        "Most people describe a tapping or knocking sensation on the scalp. Headache and scalp discomfort are the most common side effects and usually fade within the first day or two. No anesthesia is used, and over-the-counter pain relief is often enough for headaches.",
    },
    {
      question: "Can I drive after SAINT treatment?",
      answer:
        "Yes, in most cases. SAINT® does not use anesthesia or sedation, so patients can generally drive after sessions. If you experience a side effect that affects your ability to drive, tell the treatment team. A companion is optional, not required.",
    },
    {
      question: "Does SAINT require anesthesia?",
      answer:
        "No. SAINT® does not require anesthesia or sedation. Patients are fully awake throughout treatment. This is a key difference from ECT, which requires general anesthesia.",
    },
    {
      question: "What happens after the five-day SAINT protocol?",
      answer:
        "After treatment, a follow-up assessment evaluates response. A maintenance plan is discussed, which may include medication, psychotherapy, and follow-up visits. Long-term durability beyond four weeks is not yet well established, so discuss a maintenance plan with your psychiatrist.",
    },
    {
      question: "Can SAINT be repeated?",
      answer:
        "Yes. If symptoms return, SAINT® can be repeated. Some patients in the open-label study were retreated. Discuss a maintenance plan with your psychiatrist, including the possibility of another course.",
    },
    {
      question: "Who should not receive SAINT?",
      answer:
        "People with ferromagnetic metal in or near the head, implanted electronic stimulators, or who cannot have an MRI are not candidates. A history of seizures requires careful evaluation. SAINT® is not cleared for bipolar depression, PTSD, anxiety alone, OCD, or anyone under 18.",
    },
    {
      question: "Does SAINT require an MRI?",
      answer:
        "Yes. SAINT® uses a structural and functional MRI to select a personalized stimulation target. The MRI must be completed before treatment begins. People who cannot safely have an MRI are not candidates for SAINT®.",
    },
  ],
  sources: [
    {
      citation:
        "Cole EJ, et al. Stanford Neuromodulation Therapy (SNT): A Double-Blind Randomized Controlled Trial. Am J Psychiatry. 2022;179(2):132-141.",
      url: "https://pubmed.ncbi.nlm.nih.gov/35624717/",
    },
    {
      citation:
        "Cole EJ, et al. Stanford Accelerated Intelligent Neuromodulation Therapy for Treatment-Resistant Depression. Am J Psychiatry. 2020;177(8):716-726.",
      url: "https://pubmed.ncbi.nlm.nih.gov/32552917/",
    },
    {
      citation:
        "U.S. FDA. 510(k) K220177 — Magnus Neuromodulation System with SAINT Technology.",
      url: "https://www.accessdata.fda.gov/scripts/cdrh/cfdocs/cfpmn/pmn.cfm",
    },
  ],
  relatedLinks: saintQuestionRelated,
});
